import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import './Enrollments.css';
import './StudyRegistrations.css';

const LOCATION_OPTIONS = [
  { id: 'paramaribo', label: 'Paramaribo' },
  { id: 'nickerie', label: 'Nickerie' }
];

const NO_COHORT_KEY = '__geen_cohort__';
const NO_LOCATION_KEY = '__onbekend__';

const statusText = (status) => (status === 'completed' ? 'Afgerond' : status === 'cancelled' ? 'Geannuleerd' : 'Actief');
const statusClass = (status) => (status === 'completed' ? 'completed' : status === 'cancelled' ? 'cancelled' : 'active');

const StudyRegistrations = () => {
  const { t } = useTranslation();
  const { data, getCourseById } = useData();
  const { selectedLocation } = useOutletContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const { user } = useAuth();
  const isManager = user?.role === 'manager';
  const managerLocation = String(user?.location || '').toLowerCase();
  // Pagina-filter op locatie; start met de keuze uit de zijbalk (admin) of de eigen locatie (manager).
  const [locationFilter, setLocationFilter] = useState(() => {
    if (isManager) return managerLocation || 'all';
    return String(selectedLocation || 'all').toLowerCase();
  });
  const [expandedCohorts, setExpandedCohorts] = useState({});
  const [detailRegistration, setDetailRegistration] = useState(null);

  const studies = useMemo(() => data?.studies || [], [data?.studies]);
  const courses = useMemo(() => data?.courses || [], [data?.courses]);

  const toId = (value) => String(value?.id || value?._id || value || '');

  const getCourseStudyYearId = useCallback((course) => {
    return toId(course?.studyYear?.id || course?.studyYear?._id || course?.studyYear || course?.studyYearId);
  }, []);

  const studyYearLookup = useMemo(() => {
    const lookup = new Map();
    studies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        const yearId = toId(year.id || year._id);
        if (!yearId) return;
        lookup.set(yearId, { study, year });
      });
    });
    return lookup;
  }, [studies]);

  const moduleToYearLookup = useMemo(() => {
    const lookup = new Map();
    studies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        const yearId = toId(year.id || year._id);
        if (!yearId) return;
        (year.modules || []).forEach((mod) => {
          const moduleId = toId(mod.id || mod._id);
          if (moduleId) lookup.set(moduleId, { study, year });
        });
      });
    });
    return lookup;
  }, [studies]);

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  const getStudyOptionLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} ${cohortNames.join(', ')}` : study.name;
  };

  const courseMetadata = useMemo(() => {
    const entries = [];

    courses.forEach((course) => {
      const directYearId = getCourseStudyYearId(course);
      let linkedYear = studyYearLookup.get(directYearId);
      if (!linkedYear) {
        const mids = [
          toId(course?.module?.id || course?.module?._id || course?.module),
          ...(course?.modules || []).map((m) => toId(m?.id || m?._id || m))
        ].filter(Boolean);
        for (const mid of mids) {
          const entry = moduleToYearLookup.get(mid);
          if (entry) { linkedYear = entry; break; }
        }
      }
      if (!linkedYear) return;

      const { study, year } = linkedYear;
      const yearId = toId(year.id || year._id);
      const linkedModules = Array.isArray(course.modules) && course.modules.length > 0
        ? course.modules
        : (course.module ? [course.module] : []);

      if (linkedModules.length === 0) {
        entries.push({
          id: String(course.id || course._id),
          studyId: String(study.id || study._id),
          studyName: study.name,
          studyLocation: study.location,
          yearId,
          yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
          yearNumber: year.yearNumber || year.year,
          moduleId: `course-${String(course.id || course._id)}-default`,
          moduleName: 'Module nog niet ingesteld',
          courseName: course.name,
          courseCode: course.code,
          courseLocation: course.location
        });
        return;
      }

      linkedModules.forEach((moduleDoc) => {
        const moduleId = String(moduleDoc?.id || moduleDoc?._id || moduleDoc || '');
        entries.push({
          id: String(course.id || course._id),
          studyId: String(study.id || study._id),
          studyName: study.name,
          studyLocation: study.location,
          yearId,
          yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
          yearNumber: year.yearNumber || year.year,
          moduleId,
          moduleName: moduleDoc?.name || moduleDoc?.code || 'Module',
          courseName: course.name,
          courseCode: course.code,
          courseLocation: course.location
        });
      });
    });

    return entries;
  }, [courses, getCourseStudyYearId, studyYearLookup, moduleToYearLookup]);

  const courseMetadataById = useMemo(() => {
    const mapping = new Map();
    const ordered = [...courseMetadata].sort((a, b) => Number(a.yearNumber || 0) - Number(b.yearNumber || 0));
    ordered.forEach((entry) => {
      if (!mapping.has(entry.id)) {
        mapping.set(entry.id, entry);
      }
    });
    return mapping;
  }, [courseMetadata]);

  const studiesWithAssignments = useMemo(() => {
    const studyIds = new Set(courseMetadata.map((entry) => String(entry.studyId)));
    return studies.filter((study) => studyIds.has(String(study.id || study._id)));
  }, [courseMetadata, studies]);

  const resolvedEnrollmentRows = useMemo(() => {
    const rows = [];

    data.enrollments.forEach((enrollment) => {
      const course = getCourseById(enrollment.courseId);
      const student = data.students.find((item) => String(item.id) === String(enrollment.studentId));
      const metadata = courseMetadataById.get(String(enrollment.courseId));
      const courseYearId = getCourseStudyYearId(course);
      let linkedYear = studyYearLookup.get(courseYearId);
      if (!linkedYear) {
        const mids = [
          toId(course?.module?.id || course?.module?._id || course?.module),
          ...(course?.modules || []).map((m) => toId(m?.id || m?._id || m))
        ].filter(Boolean);
        for (const mid of mids) {
          const entry = moduleToYearLookup.get(mid);
          if (entry) {
            linkedYear = entry;
            break;
          }
        }
      }

      const fallbackStudyId = String(enrollment.studyId || linkedYear?.study?.id || linkedYear?.study?._id || '');
      const fallbackStudy = studies.find((item) => String(item.id || item._id) === fallbackStudyId);
      const resolvedStudyId = metadata?.studyId || fallbackStudyId;
      const resolvedStudyName = metadata?.studyName || fallbackStudy?.name || linkedYear?.study?.name || 'Onbekende studie';
      const resolvedYearId = metadata?.yearId || courseYearId || String(linkedYear?.year?.id || linkedYear?.year?._id || '');
      const resolvedYearLabel = metadata?.yearLabel || linkedYear?.year?.name || `Studiejaar ${Number(enrollment.currentStudyYear || linkedYear?.year?.yearNumber || 1)}`;
      const resolvedModuleId = metadata?.moduleId || `course-${String(course?.id || course?._id || enrollment.courseId)}-fallback`;
      const resolvedModuleName = metadata?.moduleName || 'Module nog niet ingesteld';
      const resolvedCourseId = String(course?.id || course?._id || enrollment.courseId || '');
      const resolvedCourseName = metadata?.courseName || course?.name || 'Onbekend vak';
      const resolvedCourseCode = metadata?.courseCode || course?.code || '';
      const resolvedCourseLocation = metadata?.courseLocation || course?.location || student?.location || '';

      rows.push({
        enrollment,
        student,
        resolvedStudyId,
        resolvedStudyName,
        resolvedYearId,
        resolvedYearLabel,
        resolvedModuleId,
        resolvedModuleName,
        resolvedCourseId,
        resolvedCourseName,
        resolvedCourseCode,
        resolvedCourseLocation
      });
    });

    return rows;
  }, [courseMetadataById, data.enrollments, data.students, getCourseById, getCourseStudyYearId, moduleToYearLookup, studies, studyYearLookup]);

  const availableYears = useMemo(() => {
    const yearMap = new Map();
    resolvedEnrollmentRows.forEach((row) => {
      if (!row.resolvedYearId) return;
      if (selectedStudy && row.resolvedStudyId !== selectedStudy) return;
      if (!yearMap.has(row.resolvedYearId)) {
        yearMap.set(row.resolvedYearId, {
          yearId: row.resolvedYearId,
          yearLabel: row.resolvedYearLabel
        });
      }
    });

    return Array.from(yearMap.values()).sort((left, right) => left.yearLabel.localeCompare(right.yearLabel, 'nl'));
  }, [resolvedEnrollmentRows, selectedStudy]);

  const availableCourses = useMemo(() => {
    const courseMap = new Map();
    resolvedEnrollmentRows.forEach((row) => {
      if (!row.resolvedCourseId) return;
      if (selectedStudy && row.resolvedStudyId !== selectedStudy) return;
      if (selectedYear && row.resolvedYearId !== selectedYear) return;
      if (!courseMap.has(row.resolvedCourseId)) {
        const label = row.resolvedCourseCode
          ? `${row.resolvedCourseCode} - ${row.resolvedCourseName}`
          : row.resolvedCourseName;
        courseMap.set(row.resolvedCourseId, {
          id: row.resolvedCourseId,
          label
        });
      }
    });

    return Array.from(courseMap.values()).sort((left, right) => left.label.localeCompare(right.label, 'nl'));
  }, [resolvedEnrollmentRows, selectedStudy, selectedYear]);

  const groupedRegistrations = useMemo(() => {
    const registrations = new Map();

    data.enrollments.forEach((enrollment) => {
      const course = getCourseById(enrollment.courseId);
      const student = data.students.find((item) => String(item.id) === String(enrollment.studentId));
      const metadata = courseMetadataById.get(String(enrollment.courseId));
      const courseYearId = getCourseStudyYearId(course);
      let linkedYear = studyYearLookup.get(courseYearId);
      if (!linkedYear) {
        const mids = [
          toId(course?.module?.id || course?.module?._id || course?.module),
          ...(course?.modules || []).map((m) => toId(m?.id || m?._id || m))
        ].filter(Boolean);
        for (const mid of mids) {
          const e = moduleToYearLookup.get(mid);
          if (e) { linkedYear = e; break; }
        }
      }
      const fallbackStudyId = String(enrollment.studyId || linkedYear?.study?.id || linkedYear?.study?._id || '');
      const fallbackStudy = studies.find((item) => String(item.id || item._id) === fallbackStudyId);

      if (!student) {
        return;
      }

      const resolvedStudyId = metadata?.studyId || fallbackStudyId;
      const resolvedStudyName = metadata?.studyName || fallbackStudy?.name || linkedYear?.study?.name || 'Onbekende studie';
      const resolvedYearId = metadata?.yearId || courseYearId || String(linkedYear?.year?.id || linkedYear?.year?._id || '');
      const resolvedYearLabel = metadata?.yearLabel || linkedYear?.year?.name || `Studiejaar ${Number(enrollment.currentStudyYear || linkedYear?.year?.yearNumber || 1)}`;
      const resolvedModuleId = metadata?.moduleId || `course-${String(course?.id || course?._id || enrollment.courseId)}-fallback`;
      const resolvedModuleName = metadata?.moduleName || 'Module nog niet ingesteld';
      const resolvedCourseName = metadata?.courseName || course?.name || 'Onbekend vak';
      const resolvedCourseCode = metadata?.courseCode || course?.code || '';
      const resolvedCourseLocation = metadata?.courseLocation || course?.location || student.location || '';

      if (!resolvedStudyId) {
        return;
      }

      // Locatie van de student is leidend; val terug op de locatie van het vak.
      const normalizedLocation = String(locationFilter || 'all').toLowerCase();
      const studentLocation = String(student.location || resolvedCourseLocation || '').toLowerCase();
      if (normalizedLocation !== 'all' && studentLocation !== normalizedLocation) {
        return;
      }

      if (selectedStudy && resolvedStudyId !== selectedStudy) {
        return;
      }

      if (selectedYear && resolvedYearId !== selectedYear) {
        return;
      }

      if (selectedStatus && enrollment.status !== selectedStatus) {
        return;
      }

      if (selectedCourseFilter && String(course?.id || course?._id || enrollment.courseId) !== String(selectedCourseFilter)) {
        return;
      }

      const haystack = [
        student.name,
        student.email,
        resolvedStudyName,
        resolvedYearLabel,
        resolvedModuleName,
        resolvedCourseName,
        resolvedCourseCode
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!haystack.includes(searchTerm.trim().toLowerCase())) {
        return;
      }

      const key = `${resolvedStudyId}:${student.id}`;
      if (!registrations.has(key)) {
        registrations.set(key, {
          key,
          studyId: resolvedStudyId,
          studyName: resolvedStudyName,
          studentId: student.id,
          student,
          locationId: studentLocation || NO_LOCATION_KEY,
          cohort: student.cohort || null,
          years: new Map(),
          modules: new Map(),
          courses: new Map(),
          activeCount: 0,
          completedCount: 0,
          cancelledCount: 0,
          latestEnrollmentDate: null,
          rows: []
        });
      }

      const registration = registrations.get(key);
      registration.years.set(resolvedYearId, resolvedYearLabel);
      registration.modules.set(resolvedModuleId, resolvedModuleName);
      registration.courses.set(String(course?.id || course?._id || enrollment.courseId), resolvedCourseName);
      registration.rows.push({
        enrollmentId: String(enrollment.id || enrollment._id),
        courseCode: resolvedCourseCode,
        courseName: resolvedCourseName,
        moduleName: resolvedModuleName,
        yearLabel: resolvedYearLabel,
        status: enrollment.status || 'active',
        enrollmentDate: enrollment.enrollmentDate ? new Date(enrollment.enrollmentDate) : null
      });

      if (enrollment.status === 'active') registration.activeCount += 1;
      if (enrollment.status === 'completed') registration.completedCount += 1;
      if (enrollment.status === 'cancelled') registration.cancelledCount += 1;

      const enrollmentDate = enrollment.enrollmentDate ? new Date(enrollment.enrollmentDate) : null;
      if (enrollmentDate && !Number.isNaN(enrollmentDate.getTime())) {
        if (!registration.latestEnrollmentDate || enrollmentDate > registration.latestEnrollmentDate) {
          registration.latestEnrollmentDate = enrollmentDate;
        }
      }
    });

    return Array.from(registrations.values())
      .sort((left, right) => left.student.name.localeCompare(right.student.name, 'nl'));
  }, [courseMetadataById, data.enrollments, data.students, getCourseById, getCourseStudyYearId, searchTerm, selectedCourseFilter, locationFilter, selectedStatus, selectedStudy, selectedYear, studies, studyYearLookup, moduleToYearLookup]);

  // Groepering: locatie -> cohort -> inschrijvingen.
  const groupedByLocation = useMemo(() => {
    const locations = new Map();
    groupedRegistrations.forEach((registration) => {
      const locationKey = registration.locationId;
      if (!locations.has(locationKey)) {
        const option = LOCATION_OPTIONS.find((item) => item.id === locationKey);
        locations.set(locationKey, {
          key: locationKey,
          label: option?.label || 'Onbekende locatie',
          cohorts: new Map()
        });
      }
      const location = locations.get(locationKey);
      const cohortId = String(registration.cohort?.id || registration.cohort?._id || NO_COHORT_KEY);
      if (!location.cohorts.has(cohortId)) {
        location.cohorts.set(cohortId, {
          key: `${locationKey}:${cohortId}`,
          id: cohortId,
          name: registration.cohort?.name || 'Zonder cohort',
          code: registration.cohort?.code || '',
          registrations: [],
          studentIds: new Set()
        });
      }
      const cohort = location.cohorts.get(cohortId);
      cohort.registrations.push(registration);
      cohort.studentIds.add(registration.studentId);
    });

    const order = (key) => {
      const index = LOCATION_OPTIONS.findIndex((item) => item.id === key);
      return index === -1 ? LOCATION_OPTIONS.length : index;
    };

    return Array.from(locations.values())
      .sort((a, b) => order(a.key) - order(b.key))
      .map((location) => ({
        ...location,
        cohorts: Array.from(location.cohorts.values()).sort((a, b) => {
          if (a.id === NO_COHORT_KEY) return 1;
          if (b.id === NO_COHORT_KEY) return -1;
          return a.name.localeCompare(b.name, 'nl', { numeric: true });
        })
      }));
  }, [groupedRegistrations]);

  const totalRegistrations = groupedRegistrations.length;
  const totalStudents = new Set(groupedRegistrations.map((registration) => registration.studentId)).size;
  const totalStudies = new Set(groupedRegistrations.map((registration) => registration.studyId)).size;
  const totalAssignments = groupedRegistrations.reduce((sum, registration) => sum + registration.courses.size, 0);

  const toggleCohort = (key) => setExpandedCohorts((prev) => ({ ...prev, [key]: !prev[key] }));
  const setAllCohorts = (expanded) => {
    const next = {};
    groupedByLocation.forEach((location) => location.cohorts.forEach((cohort) => { next[cohort.key] = expanded; }));
    setExpandedCohorts(next);
  };

  const resetFilters = () => {
    if (!isManager) setLocationFilter('all');
    setSearchTerm('');
    setSelectedStudy('');
    setSelectedYear('');
    setSelectedCourseFilter('');
    setSelectedStatus('');
  };

  return (
    <div className="enrollments-page">
      <div className="page-header">
        <div>
          <h1>{t('admin.studyRegistrations')}</h1>
          <p className="page-subtitle">{t('admin.studyRegistrationNote')}</p>
        </div>
      </div>

      <div className="registration-stat-grid">
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalStudies}</span>
          <span className="registration-stat-label">Studies</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalStudents}</span>
          <span className="registration-stat-label">Studenten</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalRegistrations}</span>
          <span className="registration-stat-label">Studie-inschrijvingen</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalAssignments}</span>
          <span className="registration-stat-label">Actieve vakkoppelingen</span>
        </div>
      </div>

      <div className="sr-location-tabs" role="tablist" aria-label="Locatie">
        {[{ id: 'all', label: 'Alle locaties' }, ...LOCATION_OPTIONS]
          .filter((option) => !isManager || option.id === managerLocation)
          .map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={locationFilter === option.id}
              className={`sr-location-tab ${locationFilter === option.id ? 'active' : ''}`}
              onClick={() => setLocationFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
      </div>

      <div className="enrollment-filter-toolbar">
        <input
          type="search"
          className="enrollment-search-input"
          placeholder="Zoek op student, studie, module of vak"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="enrollment-select-input" value={selectedStudy} onChange={(e) => {
          setSelectedStudy(e.target.value);
          setSelectedYear('');
          setSelectedCourseFilter('');
        }}>
          <option value="">Alle studies</option>
          {studiesWithAssignments.map((study) => {
            const studyId = toId(study.id || study._id);
            return (
              <option key={studyId} value={studyId}>{getStudyOptionLabel(study)}</option>
            );
          })}
        </select>
        <select className="enrollment-select-input" value={selectedYear} onChange={(e) => {
          setSelectedYear(e.target.value);
          setSelectedCourseFilter('');
        }}>
          <option value="">Alle studiejaren</option>
          {availableYears.map((entry) => (
            <option key={entry.yearId} value={entry.yearId}>{entry.yearLabel}</option>
          ))}
        </select>
        <select className="enrollment-select-input" value={selectedCourseFilter} onChange={(e) => setSelectedCourseFilter(e.target.value)}>
          <option value="">Alle vakken</option>
          {availableCourses.map((course) => (
            <option key={course.id} value={course.id}>{course.label}</option>
          ))}
        </select>
        <select className="enrollment-select-input" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
          <option value="">Alle statussen</option>
          <option value="active">Actief</option>
          <option value="completed">Afgerond</option>
          <option value="cancelled">Geannuleerd</option>
        </select>
        <button type="button" className="btn-secondary" onClick={resetFilters}>
          Reset filters
        </button>
      </div>

      {groupedByLocation.length > 0 && (
        <div className="sr-expand-actions">
          <button type="button" className="sr-link-btn" onClick={() => setAllCohorts(true)}>Alles uitklappen</button>
          <span aria-hidden="true">·</span>
          <button type="button" className="sr-link-btn" onClick={() => setAllCohorts(false)}>Alles inklappen</button>
        </div>
      )}

      <div className="registration-groups">
        {groupedByLocation.map((location) => (
          <section key={location.key} className="sr-location-section">
            <div className="sr-location-header">
              <h2>{location.label}</h2>
              <span>
                {location.cohorts.length} cohort{location.cohorts.length === 1 ? '' : 'en'} ·{' '}
                {new Set(location.cohorts.flatMap((cohort) => Array.from(cohort.studentIds))).size} studenten
              </span>
            </div>

            <div className="sr-cohort-list">
              {location.cohorts.map((cohort) => {
                const isExpanded = !!expandedCohorts[cohort.key];
                return (
                  <div key={cohort.key} className="sr-cohort-card">
                    <button
                      type="button"
                      className="sr-cohort-toggle"
                      aria-expanded={isExpanded}
                      onClick={() => toggleCohort(cohort.key)}
                    >
                      <span>
                        <strong>{cohort.name}</strong>
                        {cohort.code && cohort.code !== cohort.name && <span className="sr-muted"> ({cohort.code})</span>}
                        <span className="sr-muted"> - {location.label}</span>
                      </span>
                      <span className="sr-cohort-count">
                        {cohort.studentIds.size} studenten {isExpanded ? '▲' : '▼'}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="registration-grid sr-cohort-body">
                        {cohort.registrations.map((registration) => (
                          <article key={registration.key} className="registration-card">
                            <div className="registration-card-header">
                              <div>
                                <h3>{registration.student.name}</h3>
                                <p>{registration.student.email}</p>
                              </div>
                            </div>
                            <p className="sr-study-name">{registration.studyName}</p>

                            <div className="registration-card-meta">
                              <div>
                                <span className="registration-meta-label">Studiejaren</span>
                                <strong>{Array.from(registration.years.values()).join(', ') || '-'}</strong>
                              </div>
                              <div>
                                <span className="registration-meta-label">Modules</span>
                                <strong>{registration.modules.size}</strong>
                              </div>
                              <div>
                                <span className="registration-meta-label">Vakken</span>
                                <strong>{registration.courses.size}</strong>
                              </div>
                            </div>

                            <div className="registration-status-row">
                              {registration.activeCount > 0 && <span className="status-badge active">Actief {registration.activeCount}</span>}
                              {registration.completedCount > 0 && <span className="status-badge completed">Afgerond {registration.completedCount}</span>}
                              {registration.cancelledCount > 0 && <span className="status-badge cancelled">Geannuleerd {registration.cancelledCount}</span>}
                            </div>

                            <div className="registration-course-list">
                              {Array.from(registration.courses.values()).slice(0, 4).map((courseName) => (
                                <span key={courseName} className="registration-course-chip">{courseName}</span>
                              ))}
                              {registration.courses.size > 4 && (
                                <span className="registration-course-chip muted">+{registration.courses.size - 4} meer</span>
                              )}
                            </div>

                            <div className="registration-card-footer">
                              <p className="registration-last-updated">
                                Laatste koppeling: {registration.latestEnrollmentDate ? registration.latestEnrollmentDate.toLocaleDateString('nl-NL') : '-'}
                              </p>
                              <button
                                type="button"
                                className="btn-expand-registrations"
                                onClick={() => setDetailRegistration(registration)}
                              >
                                Bekijk details →
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {groupedByLocation.length === 0 && (
          <p className="no-data">{t('common.noData')}</p>
        )}
      </div>

      <Modal
        isOpen={!!detailRegistration}
        onClose={() => setDetailRegistration(null)}
        title={detailRegistration?.student?.name || 'Inschrijvingsdetails'}
        position="right"
      >
        {detailRegistration && (
          <div className="sr-drawer">
            <dl className="sr-drawer-facts">
              <div><dt>E-mail</dt><dd>{detailRegistration.student.email || '-'}</dd></div>
              <div><dt>Locatie</dt><dd>{LOCATION_OPTIONS.find((item) => item.id === detailRegistration.locationId)?.label || 'Onbekend'}</dd></div>
              <div><dt>Cohort</dt><dd>{detailRegistration.cohort?.name || 'Zonder cohort'}</dd></div>
              <div><dt>Studie</dt><dd>{detailRegistration.studyName}</dd></div>
              <div><dt>Studiejaren</dt><dd>{Array.from(detailRegistration.years.values()).join(', ') || '-'}</dd></div>
              <div><dt>Laatste koppeling</dt><dd>{detailRegistration.latestEnrollmentDate ? detailRegistration.latestEnrollmentDate.toLocaleDateString('nl-NL') : '-'}</dd></div>
            </dl>

            <div className="registration-status-row">
              {detailRegistration.activeCount > 0 && <span className="status-badge active">Actief {detailRegistration.activeCount}</span>}
              {detailRegistration.completedCount > 0 && <span className="status-badge completed">Afgerond {detailRegistration.completedCount}</span>}
              {detailRegistration.cancelledCount > 0 && <span className="status-badge cancelled">Geannuleerd {detailRegistration.cancelledCount}</span>}
            </div>

            <h4 className="sr-drawer-title">Vakken ({detailRegistration.rows.length})</h4>
            <ul className="sr-drawer-courses">
              {[...detailRegistration.rows]
                .sort((a, b) => a.courseName.localeCompare(b.courseName, 'nl'))
                .map((row) => (
                  <li key={row.enrollmentId}>
                    <div className="sr-drawer-course-head">
                      <strong>{row.courseCode ? `${row.courseCode} — ` : ''}{row.courseName}</strong>
                      <span className={`status-badge ${statusClass(row.status)}`}>{statusText(row.status)}</span>
                    </div>
                    <div className="sr-muted">
                      {row.moduleName} · {row.yearLabel} · Ingeschreven op {row.enrollmentDate ? row.enrollmentDate.toLocaleDateString('nl-NL') : '-'}
                    </div>
                  </li>
                ))}
            </ul>

            <Link to="/admin/course-assignments" className="btn-primary sr-drawer-link">Bewerken in Vak Toewijzen →</Link>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StudyRegistrations;